import { reactive, ref, computed } from 'vue'
import { useChatSocket } from './useChatSocket'

/**
 * Reactive online-user state. Seeded from GET /api/chat/presence (REST) and
 * kept in sync by `presence.snapshot` + `presence.update` WS frames.
 *
 * Each online user carries a `status` field: 'online' | 'afk' | 'dnd'.
 * Offline users are not in the map (they have a separate `lastSeen` cache).
 * `status.self` is the caller's own status, echoed by the server on WS open
 * and updated locally via `setSelfStatus()`.
 */

export type PresenceStatus = 'online' | 'afk' | 'dnd'

interface OnlineUser {
    id: string
    name: string
    avatar: string | null
    role: string
    status: PresenceStatus
    // Mirrored from chatProfile.openForCollabs — updated in-place by the
    // `profile.update` WS frame so the presence list re-renders live
    // when any peer toggles the flag.
    openForCollabs?: boolean
    // Free-text "custom status" (chatProfile.customStatus). Patched live by
    // `profile.update`. Shown next to the name in the online list / strip.
    customStatus?: string | null
}

// Map<userId, OnlineUser>
const online = reactive(new Map<string, OnlineUser>())
const lastSeen = reactive(new Map<string, string>()) // userId -> ISO
const selfStatus = ref<PresenceStatus>('online')

let wired = false
let loaded = false

/* ───────────────── Auto-AFK on user-input idle ───────────────── */
//
// The WS heartbeat detects connection health (browser process alive + JS
// event loop running), NOT whether the human is actually using the app. A
// background tab with throttled JS still pongs the server's pings, so peers
// see the user as "online" forever. We supplement with a real input-activity
// tracker: 5 min without mouse/keyboard/touch flips the user to AFK; first
// activity flips them back to online. The flip is transient on the server
// (preferredStatus stays whatever the user picked) so reconnects always start
// from the user's manual choice.

const IDLE_THRESHOLD_MS = 5 * 60 * 1000
const IDLE_CHECK_INTERVAL_MS = 30 * 1000
const INTERACTION_THROTTLE_MS = 1000

let lastInteractionAt = Date.now()
let lastInteractionWriteAt = 0
let autoAfk = false
let idleCheckTimer: ReturnType<typeof setInterval> | null = null
let idleWired = false

function normalizeStatus(raw: any): PresenceStatus {
    return (raw === 'afk' || raw === 'dnd') ? raw : 'online'
}

async function loadInitialSnapshot() {
    if (loaded) return
    try {
        const res = await $fetch<{ online: (OnlineUser & { status?: string })[] }>('/api/chat/presence')
        online.clear()
        for (const u of res.online) {
            online.set(u.id, { ...u, status: normalizeStatus(u.status) })
        }
        loaded = true
    } catch (err: any) {
        // 404 from feature flag → keep empty, don't retry
        if (err?.statusCode === 404) loaded = true
    }
}

function wire() {
    if (wired) return
    wired = true
    const { on } = useChatSocket()

    on('presence.snapshot', (frame) => {
        const ids: string[] = Array.isArray(frame.onlineUserIds) ? frame.onlineUserIds : []
        // Snapshot is authoritative — drop anyone not in the snapshot
        const known = new Set(online.keys())
        for (const id of known) if (!ids.includes(id)) online.delete(id)
        // Hydrate missing ones — we need full user info. Reload snapshot.
        if (ids.some(id => !online.has(id))) {
            $fetch<{ online: (OnlineUser & { status?: string })[] }>('/api/chat/presence')
                .then(res => {
                    online.clear()
                    for (const u of res.online) online.set(u.id, { ...u, status: normalizeStatus(u.status) })
                })
                .catch(() => { /* noop */ })
        }
    })

    on('presence.update', (frame) => {
        if (!frame.userId) return
        const raw = String(frame.status || '')
        if (raw === 'online' || raw === 'afk' || raw === 'dnd') {
            const existing = online.get(frame.userId)
            if (existing) {
                // Mutate in place so reactive watchers fire on status change
                existing.status = raw
            } else if (frame.user && typeof frame.user.id === 'string') {
                // Enriched frame (server attaches serializeUser on the online
                // broadcast) — hydrate directly. This is the common path and
                // avoids a per-join refetch of the whole presence list.
                online.set(frame.userId, { ...frame.user, status: raw })
            } else {
                // Fallback only: bare frame for an unknown user (enrichment
                // failed server-side, or a mid-deploy older instance). Refetch.
                $fetch<{ online: (OnlineUser & { status?: string })[] }>('/api/chat/presence')
                    .then(res => {
                        for (const u of res.online) online.set(u.id, { ...u, status: normalizeStatus(u.status) })
                    })
                    .catch(() => { /* noop */ })
            }
        } else {
            // offline
            online.delete(frame.userId)
            if (frame.lastSeen) lastSeen.set(frame.userId, frame.lastSeen)
        }
    })

    // Live-patch public profile changes (currently just `openForCollabs`)
    // without yanking the snapshot — the server broadcasts these on the
    // tenant presence channel whenever the flag flips in settings.put.
    on('profile.update', (frame) => {
        if (!frame?.userId || !frame?.patch) return
        const existing = online.get(frame.userId)
        if (!existing) return
        const p = frame.patch
        if (typeof p.openForCollabs === 'boolean') existing.openForCollabs = p.openForCollabs
        // Display-name changes broadcast the resolved name (displayName || name)
        if (typeof p.name === 'string' && p.name) existing.name = p.name
        // customStatus may be set to a string or cleared to null
        if ('customStatus' in p) existing.customStatus = p.customStatus || null
    })

    // Server echoes the caller's own status on open (covers reconnects where
    // the preferred status was sticky but hadn't been echoed yet)
    on('status.self', (frame) => {
        const raw = String(frame.status || '')
        if (raw === 'online' || raw === 'afk' || raw === 'dnd') selfStatus.value = raw
    })

    // Resync on every WS reopen — we may have missed events during the gap
    on('__open', () => {
        loaded = false
        loadInitialSnapshot()
        // The server resends `status.self` from the persisted preferredStatus
        // on every open. Treat that as a fresh slate for auto-AFK: a stale
        // `autoAfk=true` here would block the idle detector from re-engaging
        // until the next manual status pick.
        autoAfk = false
        lastInteractionAt = Date.now()
    })
}

/**
 * Persist a new self-status to the server. Optimistically applies the change
 * locally so the UI updates instantly; the WS `presence.update` broadcast
 * reconciles other tabs.
 *
 * `transient: true` skips the server-side preferredStatus write — used by the
 * auto-AFK detector so an idle user temporarily appears AFK without changing
 * their persisted choice. A manual (non-transient) call also clears the
 * autoAfk flag so the idle detector can re-engage cleanly after the user
 * picks a different status from the UI.
 */
async function setSelfStatus(status: PresenceStatus, opts?: { transient?: boolean }): Promise<boolean> {
    const transient = !!opts?.transient
    const prev = selfStatus.value
    selfStatus.value = status
    if (!transient) autoAfk = false
    try {
        await $fetch('/api/chat/presence', {
            method: 'PUT',
            body: transient ? { status, transient: true } : { status }
        })
        return true
    } catch (err) {
        // Revert on failure so UI mirrors truth
        selfStatus.value = prev
        console.error('[chat:presence] setSelfStatus failed', err)
        return false
    }
}

function recordInteraction() {
    const now = Date.now()
    if (now - lastInteractionWriteAt < INTERACTION_THROTTLE_MS) return
    lastInteractionWriteAt = now
    lastInteractionAt = now
    // If we auto-AFK'd this user, first activity restores them to online.
    if (autoAfk && selfStatus.value === 'afk') {
        autoAfk = false
        setSelfStatus('online', { transient: true }).catch(() => { /* noop */ })
    }
}

function checkIdle() {
    if (selfStatus.value !== 'online') return
    if (autoAfk) return
    // Skip when the chat WS isn't actually connected — the server has already
    // marked the user offline (or never marked them online), so an `afk` push
    // here would just be a wasted HTTP roundtrip with no visible effect.
    if (useChatSocket().status.value !== 'open') return
    const idleMs = Date.now() - lastInteractionAt
    if (idleMs < IDLE_THRESHOLD_MS) return
    autoAfk = true
    setSelfStatus('afk', { transient: true }).catch(() => {
        // If the server rejects the transient flip, drop the autoAfk flag so
        // we don't get stuck "already auto-AFK"-blocked from re-trying.
        autoAfk = false
    })
}

function wireIdleDetection() {
    if (idleWired || typeof window === 'undefined') return
    idleWired = true
    const opts = { passive: true, capture: true } as AddEventListenerOptions
    document.addEventListener('mousemove', recordInteraction, opts)
    document.addEventListener('keydown', recordInteraction, opts)
    document.addEventListener('touchstart', recordInteraction, opts)
    document.addEventListener('scroll', recordInteraction, opts)
    document.addEventListener('click', recordInteraction, opts)
    // Tab regaining focus counts as activity (we may have been idle in another
    // tab the whole time, but the user is back now).
    window.addEventListener('focus', recordInteraction)
    idleCheckTimer = setInterval(checkIdle, IDLE_CHECK_INTERVAL_MS)
}

export function usePresence() {
    if (import.meta.client) {
        wire()
        wireIdleDetection()
        if (!loaded) loadInitialSnapshot()
    }

    // Viewer id — used to split self out of the "who else is around" surfaces.
    const { user } = useUserSession()
    const myId = computed(() => String((user.value as any)?.id || (user.value as any)?._id || ''))

    const onlineList = computed(() => Array.from(online.values()))
    // Everyone online except the viewer. The strip and the online count use
    // this (seeing your own avatar in a "who's around" list is noise); the
    // full `online` list is still available for the People tab, which pins
    // the viewer as a "You" row.
    const onlineExcludingSelf = computed(() => {
        const me = myId.value
        // Until the session resolves, myId is '' — which would match nobody and
        // briefly leak the viewer's own avatar into the strip/count. Hold the
        // list empty until we actually know who "self" is.
        if (!me) return []
        return onlineList.value.filter(u => u.id !== me)
    })
    const isOnline = (userId: string) => online.has(userId)
    const getStatus = (userId: string): PresenceStatus | 'offline' => {
        const u = online.get(userId)
        return u ? u.status : 'offline'
    }
    const getOnlineUser = (userId: string): OnlineUser | null => online.get(userId) || null
    const getLastSeen = (userId: string) => lastSeen.get(userId) || null

    return {
        online: onlineList,
        onlineExcludingSelf,
        myId,
        isOnline,
        getStatus,
        getOnlineUser,
        getLastSeen,
        selfStatus: computed(() => selfStatus.value),
        setSelfStatus,
        reload: loadInitialSnapshot
    }
}
